// Tab "Cài đặt" của khung Glass (điện thoại, 30/10/2026): gộp danh sách mục Cài đặt với mọi thứ của menu tài khoản cũ
// (ô tên đơn vị góc trái) — ảnh đại diện (chủ đơn vị bấm để đổi), đổi đơn vị / chế độ, lối sang Quản trị NPP, Bảng giá,
// Quét mã đăng nhập, Góp ý, Chính sách, cài app, Đăng xuất. Không có "Xem bản máy tính" (user bỏ
// 30/10). Dữ liệu khung lấy từ ShellContext (GlassLayout cấp), không gọi lại useShellModel. Trang Cài đặt chỉ dựng khối
// này khi đang trong khung Glass.

import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeftRight, ArrowUpRight, Bell, Camera, ChevronRight, Hand, Landmark, Lock, LogOut, MapPin, ScanLine, ShieldCheck, Store,
  Truck, Users, type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useBrand } from '@/lib/brand'
import { PageHeader } from '@/components/ui/page-header'
import { InstallAppCard } from '@/components/InstallAppCard'
import { PoweredBy, ShopTrackLogo } from '@/components/ui/logo'
import { UnitAvatar, useAvatarEditor } from '@/components/settings/ShopAvatar'
import { ModeSwitch } from './ModeSwitch'
import { UnitSwitcher } from './UnitSwitcher'
import type { ShellModel } from './shell-model'

const TAB_ICON: Record<string, LucideIcon> = {
  general: Store,
  members: Users,
  access: ShieldCheck,
  warehouse: MapPin,
  carriers: Truck,
  banks: Landmark,
  security: Lock,
  notifications: Bell,
}

const card = 'overflow-hidden rounded-xl bg-raise shadow-card'
const row = 'motion-state flex min-h-[52px] w-full items-center gap-3 border-b border-rule px-4 text-left text-sm text-ink last:border-b-0 active:bg-paperSoft'

function Row({ icon: Icon, children, trail = 'chevron', danger }: { icon: LucideIcon; children: ReactNode; trail?: 'chevron' | 'external' | 'none'; danger?: boolean }) {
  return (
    <>
      <Icon className={cn('size-[18px] shrink-0', danger ? 'text-danger' : 'text-inkSoft')} aria-hidden />
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trail === 'chevron' && <ChevronRight className="size-4 shrink-0 text-muted" aria-hidden />}
      {trail === 'external' && <ArrowUpRight className="size-4 shrink-0 text-muted" aria-hidden />}
    </>
  )
}

export function PhoneSettingsHub<T extends string>({
  shell,
  tabs,
  onOpen,
}: {
  shell: ShellModel
  /** Các mục của trang Cài đặt người này thấy (đã lọc theo cấp / nhân viên) */
  tabs: { key: T; label: string }[]
  onOpen: (key: T) => void
}) {
  const brand = useBrand()
  const avatar = useAvatarEditor()
  const { shopName, levelInfo, crossLink, units, switchUnit, auth, canSwitchMode, mode, switchMode, accountMenu, onLogout, avatarUrl, isStaff } = shell
  // Mục menu tài khoản cũ trỏ vào một mục Cài đặt đã có ở danh sách trên — không lặp
  const links = accountMenu.filter((i) => !i.to.startsWith('/settings'))

  return (
    <div className="space-y-4">
      <PageHeader title="Cài đặt" />

      <section className={cn(card, 'flex items-center gap-3 p-4')}>
        {avatar.input}
        {/* Chủ đơn vị chạm ảnh để đổi (Xóa ảnh ở Thông tin chung); nhân viên chỉ xem */}
        {isStaff ? (
          <UnitAvatar name={shopName || 'S'} src={avatarUrl} className="gx-avatar is-lg" />
        ) : (
          <button type="button" onClick={avatar.pick} disabled={avatar.busy} aria-label="Đổi ảnh đại diện" className="relative shrink-0 rounded-full disabled:opacity-60">
            <UnitAvatar name={shopName || 'S'} src={avatarUrl} className="gx-avatar is-lg" />
            <span className="absolute -bottom-0.5 -right-0.5 grid size-6 place-items-center rounded-full bg-raise text-inkSoft shadow-pop ring-1 ring-rule" aria-hidden>
              <Camera className="size-3.5" />
            </span>
          </button>
        )}
        <span className="min-w-0 flex-1">
          <b className="block truncate text-base font-semibold text-ink">{shopName || 'Tài khoản'}</b>
          <small className="block truncate text-[13px] text-muted">{levelInfo.levelTag}</small>
        </span>
      </section>

      {units.length > 1 && <UnitSwitcher units={units} activeId={auth.activeShopId} onSwitch={switchUnit} dense />}
      {canSwitchMode && <ModeSwitch mode={mode} onChange={switchMode} className="gx-mode-switch w-full" />}

      {crossLink && (
        <nav className={card}>
          <Link to={crossLink.to} className={row}>
            <Row icon={ArrowLeftRight}>{crossLink.label}</Row>
          </Link>
        </nav>
      )}

      <nav className={card} aria-label="Cài đặt">
        {tabs.map((t) => (
          <button key={t.key} type="button" onClick={() => onOpen(t.key)} className={row}>
            <Row icon={TAB_ICON[t.key] ?? Store}>{t.label}</Row>
          </button>
        ))}
      </nav>

      <nav className={card} aria-label="Khác">
        {links.map((i) => (
          <Link key={i.to} to={i.to} className={row}>
            <Row icon={i.Icon}>{i.label}</Row>
          </Link>
        ))}
        {/* Đăng nhập máy khác bằng mã QR (docs/KE_HOACH_DANG_NHAP_QR.md) */}
        <Link to="/qr" className={row}>
          <Row icon={ScanLine}>Quét mã đăng nhập</Row>
        </Link>
        <a href="/privacy" target="_blank" rel="noopener" className={row}>
          <Row icon={Hand} trail="external">Chính sách quyền riêng tư</Row>
        </a>
      </nav>

      <InstallAppCard />

      <section className={card}>
        <button type="button" onClick={onLogout} className={cn(row, 'text-danger')}>
          <Row icon={LogOut} trail="none" danger>
            Đăng xuất
          </Row>
        </button>
      </section>

      {brand && (
        <div className="flex flex-col items-center gap-1 pb-1 pt-2">
          <ShopTrackLogo place="bar" />
          <PoweredBy />
        </div>
      )}
    </div>
  )
}
